import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import Popup from '../../components/Popup';
import AutoComplete from '../../components/AutoComplete';
import DropDown from '../../components/DropDown';
import SecondaryButton from '../../components/SecondaryButton';
import PrimaryButton from '../../components/PrimaryButton';
import WizardButton from '../../components/WizardButton';
import RemoveButton from '../../components/RemoveButton';
import TextField from '../../components/TextField';
import CheckBox from '../../components/CheckBox';
import ErrorMessage from '../../components/ErrorMessage';
import { validateEmail } from '../../utils';
import {
  Wrapper,
  ContentPanel,
  GroupInvitiation,
  GroupList,
  GroupListLabel,
  GroupAddButton,
  ParticipantInvitiation,
  SearchLabel,
  ButtonPanel,
  SelectionListWrapper,
  InvitedLabel,
  EmailInvitation,
} from './styles';

import { SelectionWrapper, SelectionList } from '../MyOrganization/styles';

import { ParticipantInfoPanel, CenterWrapper, ParticipantIcon, ParticipantName } from '../MyOrganization/styles';

const InviteParticipantsPopup = ({
  popupIsOpen,
  onClose,
  onChangePage,
  userToken,
  loopId,
  flash,
  isNew,
  isTeachers,
  sessionUuid,
  isSession = !!sessionUuid,
  payload,
  invitedParticipantIds = [],
  showInvitationEmail,
}) => {
  const [organizationParticipants, setOrganizationParticipants] = useState([]);
  const [sessionParticipants, setSessionParticipants] = useState([]);
  const [invitationEmails, setInvitationEmails] = useState([]);
  const [organizationEmailPattern, setOrganizationEmailPattern] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadData, setLoadData] = useState(true);
  const [includeAllRoles, setIncludeAllRoles] = useState(false);
  const { getData, putData, postData, loading } = useApi(userToken);
  const isCollaborator = !isNew && !isSession;
  const isParticipant = !isTeachers;
  const roleFilter = includeAllRoles ? ['teacher', 'pupil'] : [isTeachers ? 'teacher' : 'pupil'];

  useEffect(() => {
    const getSessionContext = async () => {
      const response = await getData(`/organisation/circle/${loopId}/session_context/participants`);
      if (response.success) {
        const { participants, groups, organisation } = response.body;
        setOrganizationEmailPattern(organisation.email_filter_pattern);
        setOrganizationParticipants(participants);
        setGroups(groups);
      } else {
        flash(i18n.__('app_error_alert'));
      }
    };
    if (popupIsOpen && loadData) {
      getSessionContext();
      setLoadData(false);
    }
  }, [popupIsOpen, loadData]);

  useEffect(() => {
    if (email) {
      setErrorMessage('');
    }
  }, [email]);

  const changePage = (page) => {
    const newPayload = getPayload();
    onChangePage(page, newPayload);
  };

  const handleClose = () => {
    setSelectedGroupId('');
    setEmail('');
    setLoadData(false);
    onClose();
  };

  const participantFilter = (participant) =>
    !invitedParticipantIds.includes(participant.id) &&
    !sessionParticipants.includes(participant.id) &&
    roleFilter.includes(participant.role);

  const addParticipant = (id) => {
    const selectedParticipant = organizationParticipants.find((participant) => participant.id === id);
    const newParticpants = sessionParticipants.concat(selectedParticipant.id);
    setSessionParticipants(newParticpants);
  };

  const addGroupParticipants = () => {
    const selectedParticipants = new Set(sessionParticipants);
    groups
      .find((group) => group.id === selectedGroupId)
      .participants.map((participantId) =>
        organizationParticipants.find((participant) => participant.id === participantId),
      )
      .filter(participantFilter)
      .forEach((participant) => selectedParticipants.add(participant.id));
    setSessionParticipants([...selectedParticipants]);
    setSelectedGroupId('');
  };

  const removeParticipant = (id) => {
    if (typeof id === 'string') {
      const allInvitations = invitationEmails.filter((email) => email !== id);
      setInvitationEmails(allInvitations);
    } else {
      const allParticipants = sessionParticipants.filter((participantId) => participantId !== id);
      setSessionParticipants(allParticipants);
    }
  };

  const resetParticipantList = () => {
    setSessionParticipants([]);
    setInvitationEmails([]);
  };

  const getPayload = () => {
    const key = isCollaborator ? 'participants' : isParticipant ? 'participants' : 'teachers';
    const defaultParticipants = isCollaborator ? invitedParticipantIds : [];
    const participants = defaultParticipants
      .concat(sessionParticipants)
      .map((id) => `{ "type": "user", "value": "${id}"}`)
      .concat(invitationEmails.map((email) => `{ "type": "invite", "value": "${email}"}`));
    const newPayload = {
      ...payload,
      [key]: participants,
    };
    return newPayload;
  };

  const saveInvitations = async (activate) => {
    const newPayload = { ...getPayload(), activate };
    const url = isSession ? `/session/${sessionUuid}` : `/my-loops/${loopId}/collaborators`;
    const response = await (isNew ? postData(`/session/${loopId}`, newPayload) : putData(url, newPayload));
    if (response.redirect) {
      onClose(response.url);
    } else if (response.success) {
      handleClose();
      window.location.reload();
    } else {
      if (response.code === 401) {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else {
        flash(i18n.__('app_error_alert'));
      }
    }
  };

  const onKeyPress = (ev) => {
    if (ev.code === 'Enter') {
      if (validateEmail(email, organizationEmailPattern)) {
        const newInvitations = invitationEmails.concat(email);
        newInvitations.sort();
        setInvitationEmails(newInvitations);
        setEmail('');
      } else {
        setErrorMessage(i18n.__('invalid_email'));
      }
    }
  };

  const invitationlist = invitationEmails.map((email) => ({
    id: email,
    firstName: email,
    lastName: '',
    image: '',
  }));

  const participantList = (organizationParticipants || [])
    .filter((participant) => sessionParticipants.includes(participant.id))
    .concat(invitationlist)
    .map((participant) => {
      const { id, firstName, lastName, image, online, active_at } = participant;
      return (
        <ParticipantInfoPanel key={id}>
          <CenterWrapper>
            <ParticipantIcon
              active={true}
              icon={image}
              online={online}
              title={active_at && i18n.__('organisations.active_at', active_at)}
            />
            <ParticipantName>{`${firstName} ${lastName}`}</ParticipantName>
          </CenterWrapper>
          <RemoveButton onClick={() => removeParticipant(id)} />
        </ParticipantInfoPanel>
      );
    });

  const roleFilterPanel = (
    <CheckBox
      name="includeAllRoles"
      value={1}
      text={i18n.__('session_role_filter')}
      defaultChecked={includeAllRoles}
      onChange={(ev) => {
        const checked = ev.target.checked;
        setIncludeAllRoles(checked);
      }}
    />
  );

  const contentPanel = (
    <ContentPanel>
      {roleFilterPanel}
      <GroupInvitiation>
        <GroupListLabel id="groupsLabel">{i18n.__('fromAGroup')}</GroupListLabel>
        <GroupList>
          <DropDown
            key={Date.now()}
            aria-labelledby="groupsLabel"
            id="groups"
            maxWidth={417}
            isSearchable={false}
            items={groups.map((item) => ({ value: item.id, label: item.name }))}
            selectedValue={selectedGroupId}
            onChange={(ev) => setSelectedGroupId(ev.target.value)}
          />
          <GroupAddButton>
            <PrimaryButton text={i18n.__('app_add')} onClick={() => addGroupParticipants()} />
          </GroupAddButton>
        </GroupList>
      </GroupInvitiation>
      <ParticipantInvitiation>
        <SearchLabel id="searchLabel">{i18n.__('bySearchingYourOrganizaiton')}</SearchLabel>
        <AutoComplete
          id="availableParticipants"
          maxWidth={417}
          aria-labelledby="searchLabel"
          items={organizationParticipants.filter(participantFilter).map((participant) => ({
            value: participant.id,
            label: `${participant.firstName} ${participant.lastName}`,
          }))}
          placeholder={
            isCollaborator
              ? i18n.__('addCollaboratorNamePlaceholder')
              : isTeachers
                ? i18n.__('addTeacherNamePlaceholder')
                : i18n.__('addStudentNamePlaceholder')
          }
          onChange={(id) => {
            addParticipant(id);
          }}
        />
      </ParticipantInvitiation>
      {showInvitationEmail && (
        <EmailInvitation>
          <TextField
            id="email"
            key={invitationEmails.length}
            label={i18n.__('session_invite_by_email')}
            placeholder={i18n.__('session_invite_by_email_placeholder')}
            defaultValue={email}
            onChange={(ev) => {
              setEmail(ev.target.value.trim());
            }}
            onKeyPress={onKeyPress}
          >
            {errorMessage && <ErrorMessage message={errorMessage} />}
          </TextField>
        </EmailInvitation>
      )}
      <InvitedLabel id="invitedLabel">
        {isCollaborator
          ? i18n.__('addedCollaborators')
          : isTeachers
            ? i18n.__('invitedTeachers')
            : i18n.__('invitedParticipants')}{' '}
        ({participantList.length})
      </InvitedLabel>
      <SelectionWrapper>
        <SelectionListWrapper showInvitationEmail={showInvitationEmail}>
          <SelectionList aria-labelledby="invitedLabel">{participantList}</SelectionList>
          <PrimaryButton text={i18n.__('app_reset')} onClick={() => resetParticipantList()} />
        </SelectionListWrapper>
      </SelectionWrapper>
    </ContentPanel>
  );

  const buttonPanel = (
    <ButtonPanel isNew={isNew}>
      {isNew ? (
        isTeachers ? (
          <>
            <WizardButton
              direction="left"
              secondary={false}
              text={i18n.__('session_invite_students')}
              onClick={() => changePage('participants')}
              disabled={loading}
            />
            <Wrapper>
              <PrimaryButton
                className="start-later"
                text={i18n.__('session_start_later')}
                onClick={() => saveInvitations(false)}
                disabled={loading}
              />
              <SecondaryButton
                text={i18n.__('session_start_now')}
                onClick={() => saveInvitations(true)}
                disabled={loading}
              />
            </Wrapper>
          </>
        ) : (
          <>
            <WizardButton
              direction="left"
              secondary={false}
              text={i18n.__('session_edit_header')}
              onClick={() => changePage('settings')}
              disabled={loading}
            />
            <WizardButton
              text={i18n.__('session_invite_teachers')}
              onClick={() => changePage('teachers')}
              disabled={loading}
            />
          </>
        )
      ) : (
        <SecondaryButton
          text={isCollaborator ? i18n.__('app_save') : i18n.__('app_invite')}
          onClick={() => saveInvitations()}
          disabled={loading || participantList.length === 0}
        />
      )}
    </ButtonPanel>
  );

  return (
    <Popup
      width={600}
      popupIsOpen={popupIsOpen}
      id={isTeachers ? 'inviteTeachersPopup' : 'inviteParticipantsPopup'}
      heading={
        isCollaborator
          ? i18n.__('add_collaborator_header')
          : isTeachers
            ? i18n.__('session_invite_teachers')
            : i18n.__('session_invite_students')
      }
      contentPanel={contentPanel}
      buttonPanel={buttonPanel}
      onClose={handleClose}
    />
  );
};

export default InviteParticipantsPopup;
